import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const interests = [
  "MFInsure® Group",
  "MFInsure® PL (Life & Pension)",
  "MFInsure® PC (Property & Casualty)",
  "MFInsure® PF (Pension & Fund)",
  "Partnership / Other",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    interest: interests[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      toast.success("Thanks — we'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", role: "", interest: interests[0], message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Invalid input.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-ink text-white relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="mb-6">
              <span className="overline text-copper">Connect with us</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.03]">
              Ready to reimagine your
              <br />
              insurance software?
            </h2>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed mt-7 max-w-md">
              Talk to us. We're result-oriented — our in-depth understanding of
              the platform's features and underlying technologies guarantees the
              success of every customer implementation.
            </p>

            <div className="mt-12 space-y-6 text-sm">
              <div className="flex items-start gap-4">
                <Mail className="h-4 w-4 text-copper mt-1" />
                <div>
                  <div className="overline text-slate-500 mb-1">Email</div>
                  <a href="mailto:innovate@gradatim.co.in" className="text-white hover:text-copper" data-testid="contact-email-link">
                    innovate@gradatim.co.in
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="h-4 w-4 text-copper mt-1" />
                <div>
                  <div className="overline text-slate-500 mb-1">Offices</div>
                  <p className="text-white leading-relaxed">India · Singapore</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            {success ? (
              <div data-testid="contact-success" className="border border-white/15 bg-white/[0.03] p-10 md:p-14 flex flex-col items-start">
                <CheckCircle2 className="h-10 w-10 text-copper mb-6" strokeWidth={1.5} />
                <h3 className="font-display text-3xl md:text-4xl tracking-tight">Thank you.</h3>
                <p className="text-slate-300 mt-4 max-w-md leading-relaxed">
                  Your enquiry has been recorded. A member of our team will be in
                  touch within one business day.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  data-testid="contact-send-another"
                  className="mt-8 inline-flex items-center gap-2 text-sm text-copper link-underline"
                >
                  Send another enquiry <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                noValidate
                data-testid="contact-form"
                className="border border-white/15 bg-white/[0.02] backdrop-blur-sm p-8 md:p-10 space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Full name *" testId="contact-input-name">
                    <input
                      data-testid="contact-name"
                      required
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      className="w-full bg-transparent border-b border-white/20 focus:border-copper py-2 text-white outline-none transition-colors"
                    />
                  </Field>
                  <Field label="Work email *" testId="contact-input-email">
                    <input
                      data-testid="contact-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className="w-full bg-transparent border-b border-white/20 focus:border-copper py-2 text-white outline-none transition-colors"
                    />
                  </Field>
                  <Field label="Company" testId="contact-input-company">
                    <input
                      data-testid="contact-company"
                      type="text"
                      value={form.company}
                      onChange={update("company")}
                      className="w-full bg-transparent border-b border-white/20 focus:border-copper py-2 text-white outline-none transition-colors"
                    />
                  </Field>
                  <Field label="Your role" testId="contact-input-role">
                    <input
                      data-testid="contact-role"
                      type="text"
                      value={form.role}
                      onChange={update("role")}
                      className="w-full bg-transparent border-b border-white/20 focus:border-copper py-2 text-white outline-none transition-colors"
                    />
                  </Field>
                </div>

                <Field label="I'm interested in" testId="contact-input-interest">
                  <select
                    data-testid="contact-interest"
                    value={form.interest}
                    onChange={update("interest")}
                    className="w-full bg-ink border-b border-white/20 focus:border-copper py-2 text-white outline-none appearance-none"
                  >
                    {interests.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </Field>

                <Field label="How can we help? *" testId="contact-input-message">
                  <textarea
                    data-testid="contact-message"
                    required
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    className="w-full bg-transparent border-b border-white/20 focus:border-copper py-2 text-white outline-none resize-none transition-colors"
                  />
                </Field>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="group inline-flex items-center gap-3 bg-copper hover:bg-copper-600 disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 text-sm tracking-wide transition-colors"
                  >
                    {loading ? "Sending…" : "Send enquiry"}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <p className="overline text-slate-500 mt-6">
                    We respond within one business day · GDPR aligned
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="overline text-slate-400 block mb-3">{label}</span>
    {children}
  </label>
);
